import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getThemeColors, spacing, typography, borderRadius, getShadowStyle } from '../../utils/theme';
import UserDashboardHeader from '../../components/UserDashboardHeader';
import { API_ENDPOINTS } from '../../config/api';

interface Chama {
  id: string;
  name: string;
  category?: string;
  type?: string;
  county?: string;
  town?: string;
  currentMembers?: number;
  maxMembers?: number;
  contributionAmount?: number;
  contributionFrequency?: string;
  totalFunds?: number;
  description?: string;
  createdAt?: string;
}

const MyChamas: React.FC = () => {
  const navigate = useNavigate();
  const colors = getThemeColors('dark');

  const [chamas, setChamas] = useState<Chama[]>([]);
  const [filteredChamas, setFilteredChamas] = useState<Chama[]>([]);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedFilter, setSelectedFilter] = useState('all');
  const [sortBy, setSortBy] = useState('recent');
  const [selectedCategory, setSelectedCategory] = useState('all'); // 'all', 'chama', 'contribution'
  const [fabPosition, setFabPosition] = useState({ x: window.innerWidth - 72, y: 140 }); // Default position on the right
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });

  // Simplified filters - only show the main category tabs
  const getCurrentFilters = () => {
    return [{ id: 'all', name: 'All', icon: '📱' }];
  };

  const sortOptions = [
    { id: 'recent', name: 'Most Recent', icon: '🕒' },
    { id: 'name', name: 'Name A-Z', icon: '📝' },
    { id: 'members', name: 'Most Members', icon: '👥' },
    { id: 'funds', name: 'Highest Funds', icon: '💰' },
  ];

  useEffect(() => {
    loadUserChamas();
  }, []);

  useEffect(() => {
    applyFiltersAndSort();
  }, [chamas, searchQuery, selectedFilter, sortBy, selectedCategory]);

  // Reset selectedFilter when category changes
  useEffect(() => {
    setSelectedFilter('all');
  }, [selectedCategory]);

  const loadUserChamas = async () => {
    try {
      setLoading(true);
      const authToken = localStorage.getItem('authToken');

      if (!authToken) {
        console.error('❌ MyChamas: No auth token found');
        setChamas([]);
        return;
      }

      const response = await fetch(API_ENDPOINTS.MY_CHAMAS, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json',
        },
      });

      const data = await response.json();

      if (data.success) {
        setChamas(data.data || []);
      } else {
        console.error('❌ MyChamas: Failed to load user chamas:', data.error);
        console.error('❌ MyChamas: Full error response:', data);
        // Show user-friendly error message
        alert(`Failed to load your chamas: ${data.error || 'Unknown error'}`);
        setChamas([]);
      }
    } catch (error) {
      console.error('❌ MyChamas: Error loading user chamas:', error);
      // Show user-friendly error message for network issues
      alert('Network error: Unable to connect to the server. Please check your internet connection and try again.');
      setChamas([]);
    } finally {
      setLoading(false);
    }
  };

  const onRefresh = async () => {
    setRefreshing(true);
    await loadUserChamas();
    setRefreshing(false);
  };

  const applyFiltersAndSort = () => {
    let filtered = [...chamas];

    // Apply category filter first (chama vs contribution)
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(chama => {
        const isContribution = chama.category === 'contribution';
        return selectedCategory === 'contribution' ? isContribution : !isContribution;
      });
    }

    // Apply search filter
    if (searchQuery) {
      filtered = filtered.filter(chama =>
        chama.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        chama.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        chama.county?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        chama.town?.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    // Apply type filter
    if (selectedFilter !== 'all') {
      filtered = filtered.filter(chama => chama.type === selectedFilter);
    }

    // Apply sorting
    switch (sortBy) {
      case 'name':
        filtered.sort((a, b) => a.name.localeCompare(b.name));
        break;
      case 'members':
        filtered.sort((a, b) => (b.currentMembers || 0) - (a.currentMembers || 0));
        break;
      case 'funds':
        filtered.sort((a, b) => (b.totalFunds || 0) - (a.totalFunds || 0));
        break;
      case 'recent':
      default:
        filtered.sort((a, b) => new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime());
        break;
    }

    setFilteredChamas(filtered);
  };

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('en-KE', {
      style: 'currency',
      currency: 'KES',
      minimumFractionDigits: 0,
    }).format(amount || 0);
  };

  const getUserRole = (chama: Chama): string => {
    // For now, return 'member' as default
    // In the future, this should be fetched from the chama membership data
    if (chama.id === '1') {
      return 'chairperson';
    }
    return 'member';
  };

  const handleChamaAction = (action: string, chamaId?: string) => {

    // Use React Router navigation instead of window.location.href
    switch (action) {
      case 'view':
        navigate(`/dashboard/chama/${chamaId}`);
        break;
      case 'details':
        navigate(`/dashboard/chama/${chamaId}/details`);
        break;
      case 'manage':
        navigate(`/dashboard/chama/${chamaId}/settings`);
        break;
      case 'create':
        navigate('/dashboard/create-chama');
        break;
      case 'browse':
        navigate('/dashboard/chama-list');
        break;
      default:
        console.log(`❌ MyChamas: Unknown action: ${action}`);
    }
  };

  // Handle drag start
  const handleDragStart = (e: React.MouseEvent | React.TouchEvent) => {
    setIsDragging(true);
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();

    // Handle both mouse and touch events
    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
    const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;

    setDragOffset({
      x: clientX - rect.left,
      y: clientY - rect.top,
    });
  };

  // Handle drag move
  const handleDragMove = (e: MouseEvent | TouchEvent) => {
    if (!isDragging) return;

    // Handle both mouse and touch events
    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
    const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;

    const newX = clientX - dragOffset.x;
    const newY = clientY - dragOffset.y;

    // Constrain to viewport bounds with some padding
    const maxX = window.innerWidth - 72; // button width + padding
    const maxY = window.innerHeight - 72; // button height + padding

    setFabPosition({
      x: Math.max(8, Math.min(newX, maxX)), // Minimum 8px from edges
      y: Math.max(88, Math.min(newY, maxY)), // Don't go above header (80px + 8px padding)
    });
  };

  // Handle drag end
  const handleDragEnd = () => {
    setIsDragging(false);
  };

  // Add global mouse and touch event listeners for dragging
  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleDragMove);
      document.addEventListener('mouseup', handleDragEnd);
      document.addEventListener('touchmove', handleDragMove, { passive: false });
      document.addEventListener('touchend', handleDragEnd);
    } else {
      document.removeEventListener('mousemove', handleDragMove);
      document.removeEventListener('mouseup', handleDragEnd);
      document.removeEventListener('touchmove', handleDragMove);
      document.removeEventListener('touchend', handleDragEnd);
    }

    return () => {
      document.removeEventListener('mousemove', handleDragMove);
      document.removeEventListener('mouseup', handleDragEnd);
      document.removeEventListener('touchmove', handleDragMove);
      document.removeEventListener('touchend', handleDragEnd);
    };
  }, [isDragging, dragOffset]);

  const styles: { [key: string]: React.CSSProperties } = {
    container: {
      minHeight: '100vh',
      backgroundColor: colors.background,
      paddingBottom: 80, // Space for tab bar
    },
    scrollableContent: {
      flex: 1,
      overflowY: 'auto',
      paddingTop: 80, // Space for fixed header
      paddingBottom: 80, // Space for fixed tab bar
    },
    content: {
      maxWidth: '800px',
      margin: '0 auto',
      padding: spacing.md,
    },
    header: {
      textAlign: 'center',
      marginBottom: spacing.xl,
    },
    title: {
      fontSize: typography.fontSize['2xl'],
      fontWeight: typography.fontWeight.bold,
      color: colors.text,
      marginBottom: spacing.sm,
    },
    subtitle: {
      fontSize: typography.fontSize.base,
      color: colors.textSecondary,
    },
    searchBar: {
      backgroundColor: colors.surface,
      borderRadius: borderRadius.lg,
      padding: spacing.md,
      marginBottom: spacing.lg,
      ...getShadowStyle('md'),
      border: `1px solid ${colors.border}`,
    },
    searchInput: {
      width: '100%',
      padding: spacing.md,
      borderRadius: borderRadius.md,
      border: `1px solid ${colors.border}`,
      backgroundColor: colors.surface,
      color: colors.text,
      fontSize: typography.fontSize.base,
      outline: 'none',
    },
    filtersSection: {
      backgroundColor: colors.surface,
      borderRadius: borderRadius.lg,
      padding: spacing.lg,
      marginBottom: spacing.lg,
      ...getShadowStyle('md'),
      border: `1px solid ${colors.border}`,
    },
    categoryTabs: {
      display: 'flex',
      gap: spacing.sm,
      marginBottom: spacing.lg,
      flexWrap: 'wrap',
    },
    categoryTab: {
      padding: `${spacing.md} ${spacing.lg}`,
      borderRadius: borderRadius.lg,
      border: `1px solid ${colors.border}`,
      backgroundColor: 'transparent',
      color: colors.text,
      cursor: 'pointer',
      fontSize: typography.fontSize.sm,
      fontWeight: typography.fontWeight.medium,
      display: 'flex',
      alignItems: 'center',
      gap: spacing.sm,
      transition: 'all 0.2s ease',
    },
    activeCategoryTab: {
      backgroundColor: colors.primary,
      borderColor: colors.primary,
      color: colors.white,
    },
    contributionTab: {
      backgroundColor: colors.success,
      borderColor: colors.success,
      color: colors.white,
    },
    filtersRow: {
      display: 'flex',
      gap: spacing.sm,
      alignItems: 'center',
      flexWrap: 'wrap',
    },
    filterChip: {
      display: 'flex',
      alignItems: 'center',
      gap: spacing.sm,
      padding: `${spacing.sm} ${spacing.md}`,
      borderRadius: borderRadius.full,
      border: `1px solid ${colors.border}`,
      backgroundColor: colors.surface,
      color: colors.text,
      cursor: 'pointer',
      fontSize: typography.fontSize.sm,
      fontWeight: typography.fontWeight.medium,
      transition: 'all 0.2s ease',
    },
    activeFilterChip: {
      backgroundColor: colors.primary,
      borderColor: colors.primary,
      color: colors.white,
    },
    sortButton: {
      display: 'flex',
      alignItems: 'center',
      gap: spacing.sm,
      padding: `${spacing.sm} ${spacing.md}`,
      borderRadius: borderRadius.full,
      border: `1px solid ${colors.border}`,
      backgroundColor: colors.surface,
      color: colors.text,
      cursor: 'pointer',
      fontSize: typography.fontSize.sm,
      fontWeight: typography.fontWeight.medium,
      marginLeft: 'auto',
    },
    chamasGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))',
      gap: spacing.lg,
    },
    chamaCard: {
      backgroundColor: colors.surface,
      borderRadius: borderRadius.lg,
      padding: spacing.lg,
      ...getShadowStyle('md'),
      border: `1px solid ${colors.border}`,
      borderLeftWidth: 4,
      transition: 'all 0.2s ease',
    },
    chamaCardContribution: {
      borderLeftColor: colors.success,
    },
    chamaCardChama: {
      borderLeftColor: colors.primary,
    },
    categoryBadge: {
      alignSelf: 'flex-start',
      display: 'flex',
      alignItems: 'center',
      gap: spacing.sm,
      padding: `${spacing.xs} ${spacing.sm}`,
      borderRadius: borderRadius.lg,
      fontSize: typography.fontSize.xs,
      fontWeight: typography.fontWeight.bold,
      textTransform: 'uppercase',
      letterSpacing: 0.5,
      marginBottom: spacing.sm,
      width: 'fit-content',
    },
    chamaHeader: {
      display: 'flex',
      alignItems: 'center',
      marginBottom: spacing.md,
    },
    chamaAvatar: {
      width: 50,
      height: 50,
      borderRadius: borderRadius.xl,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      marginRight: spacing.md,
      fontSize: 24,
    },
    chamaInfo: {
      flex: 1,
    },
    chamaName: {
      fontSize: typography.fontSize.lg,
      fontWeight: typography.fontWeight.semibold,
      color: colors.text,
      margin: 0,
      marginBottom: spacing.xs,
    },
    chamaType: {
      fontSize: typography.fontSize.sm,
      color: colors.textSecondary,
      margin: 0,
    },
    roleBadge: {
      padding: `${spacing.xs} ${spacing.sm}`,
      borderRadius: borderRadius.md,
      fontSize: typography.fontSize.xs,
      fontWeight: typography.fontWeight.bold,
      textTransform: 'uppercase',
    },
    chamaStats: {
      marginBottom: spacing.md,
    },
    statItem: {
      display: 'flex',
      alignItems: 'center',
      marginBottom: spacing.sm,
    },
    statText: {
      fontSize: typography.fontSize.sm,
      color: colors.text,
      marginLeft: spacing.sm,
    },
    chamaActions: {
      display: 'flex',
      gap: spacing.sm,
      flexWrap: 'wrap',
    },
    actionButton: {
      flex: 1,
      minWidth: 100,
      padding: spacing.sm,
      borderRadius: borderRadius.md,
      border: 'none',
      fontSize: typography.fontSize.sm,
      fontWeight: typography.fontWeight.medium,
      cursor: 'pointer',
      transition: 'all 0.2s ease',
    },
    viewButton: {
      backgroundColor: colors.primary,
      color: colors.white,
    },
    manageButton: {
      backgroundColor: colors.surface,
      color: colors.text,
      border: `1px solid ${colors.border}`,
    },
    emptyState: {
      textAlign: 'center',
      padding: spacing.xxxl,
    },
    emptyIcon: {
      fontSize: 64,
      color: colors.textTertiary,
      marginBottom: spacing.lg,
    },
    emptyTitle: {
      fontSize: typography.fontSize.xl,
      fontWeight: typography.fontWeight.semibold,
      color: colors.text,
      marginBottom: spacing.sm,
    },
    emptySubtitle: {
      fontSize: typography.fontSize.base,
      color: colors.textSecondary,
      marginBottom: spacing.xl,
    },
    emptyActions: {
      display: 'flex',
      flexDirection: 'column',
      gap: spacing.md,
      maxWidth: '300px',
      margin: '0 auto',
    },
    emptyButton: {
      padding: spacing.md,
      borderRadius: borderRadius.md,
      fontSize: typography.fontSize.base,
      fontWeight: typography.fontWeight.semibold,
      cursor: 'pointer',
      border: 'none',
    },
    primaryButton: {
      backgroundColor: colors.primary,
      color: colors.white,
    },
    secondaryButton: {
      backgroundColor: 'transparent',
      color: colors.primary,
      border: `1px solid ${colors.primary}`,
    },
    tertiaryButton: {
      backgroundColor: colors.secondary,
      color: colors.white,
    },
    fab: {
      position: 'fixed',
      top: fabPosition.y,
      left: fabPosition.x,
      width: 56,
      height: 56,
      borderRadius: 28,
      backgroundColor: colors.primary,
      border: 'none',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      cursor: isDragging ? 'grabbing' : 'grab',
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
      zIndex: 1000,
      userSelect: 'none',
      transition: isDragging ? 'none' : 'all 0.2s ease',
    },
  };

  // Calculate counts for each category
  const chamaCount = chamas.filter(c => c.category !== 'contribution').length;
  const contributionCount = chamas.filter(c => c.category === 'contribution').length;

  const renderChamaCard = (chama: Chama) => {
    const userRole = getUserRole(chama);

    // Determine if it's a chama or contribution group
    const isContributionGroup = chama.category === 'contribution';

    // Different styling for each type
    const typeConfig = isContributionGroup ? {
      color: colors.success,
      icon: '❤️',
      label: 'Fund Group',
      bgColor: colors.success + '15',
      borderColor: colors.success + '30'
    } : {
      color: colors.primary,
      icon: '👥',
      label: 'Chama',
      bgColor: colors.primary + '15',
      borderColor: colors.primary + '30'
    };

    return (
      <div
        key={chama.id}
        style={{
          ...styles.chamaCard,
          ...(isContributionGroup ? styles.chamaCardContribution : styles.chamaCardChama),
          backgroundColor: typeConfig.bgColor,
        }}
      >
        {/* Category Badge */}
        <div style={{
          ...styles.categoryBadge,
          backgroundColor: typeConfig.color,
          color: colors.white,
        }}>
          <span>{typeConfig.icon}</span>
          <span>{typeConfig.label}</span>
        </div>

        <div style={styles.chamaHeader}>
          <div style={{
            ...styles.chamaAvatar,
            backgroundColor: typeConfig.color,
          }}>
            <span>{typeConfig.icon}</span>
          </div>

          <div style={styles.chamaInfo}>
            <h3 style={styles.chamaName}>{chama.name}</h3>
            <p style={styles.chamaType}>
              {chama.type} • {chama.county}, {chama.town}
            </p>
          </div>

          <div style={{
            ...styles.roleBadge,
            backgroundColor: userRole === 'chairperson' ? colors.warning + '20' : colors.info + '20',
            color: userRole === 'chairperson' ? colors.warning : colors.info,
          }}>
            {userRole.toUpperCase()}
          </div>
        </div>

        <div style={styles.chamaStats}>
          <div style={styles.statItem}>
            <span>👥</span>
            <span style={styles.statText}>
              {chama.currentMembers}/{chama.maxMembers} members
            </span>
          </div>

          <div style={styles.statItem}>
            <span>💰</span>
            <span style={styles.statText}>
              {formatCurrency(chama.contributionAmount || 0)} {chama.contributionFrequency}
            </span>
          </div>

          <div style={styles.statItem}>
            <span>📈</span>
            <span style={styles.statText}>
              {formatCurrency(chama.totalFunds || 0)} total
            </span>
          </div>
        </div>

        <div style={styles.chamaActions}>
          <button
            style={{ ...styles.actionButton, ...styles.viewButton }}
            onClick={() => handleChamaAction('details', chama.id)}
          >
            View Details
          </button>
          <button
            style={{ ...styles.actionButton, ...styles.manageButton }}
            onClick={() => handleChamaAction('view', chama.id)}
          >
            Go to Dashboard
          </button>
        </div>
      </div>
    );
  };

  const renderEmptyState = () => (
    <div style={styles.emptyState}>
      <div style={styles.emptyIcon}>👥</div>
      <div style={styles.emptyTitle}>No chamas found</div>
      <div style={styles.emptySubtitle}>
        {searchQuery || selectedFilter !== 'all'
          ? 'Try adjusting your search or filters'
          : "You haven't joined any chamas yet"
        }
      </div>

      <div style={styles.emptyActions}>
        <button
          style={{ ...styles.emptyButton, ...styles.primaryButton }}
          onClick={() => handleChamaAction('create')}
        >
          {searchQuery || selectedFilter !== 'all' ? "Clear Filters" : "Explore Chamas"}
        </button>
        <button
          style={{ ...styles.emptyButton, ...styles.secondaryButton }}
          onClick={() => handleChamaAction('browse')}
        >
          Browse Chamas
        </button>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.header}>
          <h1 style={styles.title}>My Chamas</h1>
          <p style={styles.subtitle}>Loading your groups...</p>
        </div>
        {/* Loading skeleton */}
        <div style={styles.chamasGrid}>
          {[1, 2, 3].map((i) => (
            <div key={i} style={{
              ...styles.chamaCard,
              backgroundColor: colors.surface,
              opacity: 0.7,
            }}>
              <div style={{
                height: 20,
                backgroundColor: colors.border,
                borderRadius: borderRadius.sm,
                marginBottom: spacing.sm,
                width: '60%',
              }} />
              <div style={{
                height: 16,
                backgroundColor: colors.border,
                borderRadius: borderRadius.sm,
                marginBottom: spacing.sm,
                width: '40%',
              }} />
              <div style={{
                height: 16,
                backgroundColor: colors.border,
                borderRadius: borderRadius.sm,
                width: '80%',
              }} />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <UserDashboardHeader
        title="My Chamas"
        showBackButton={false}
        showHomeButton={false}
      />

      <div style={styles.scrollableContent}>
        <div style={styles.content}>
          {/* Search Bar */}
          <div style={styles.searchBar}>
            <input
              type="text"
              style={styles.searchInput}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search your groups..."
            />
          </div>

          {/* Filters Section */}
          <div style={styles.filtersSection}>
            {/* Category Tabs */}
            <div style={styles.categoryTabs}>
              <button
                style={{
                  ...styles.categoryTab,
                  ...(selectedCategory === 'all' ? styles.activeCategoryTab : {}),
                }}
                onClick={() => setSelectedCategory('all')}
              >
                <span>📱</span>
                <span>All ({chamas.length})</span>
              </button>

              <button
                style={{
                  ...styles.categoryTab,
                  ...(selectedCategory === 'chama' ? styles.activeCategoryTab : {}),
                }}
                onClick={() => setSelectedCategory('chama')}
              >
                <span>👥</span>
                <span>Chamas ({chamaCount})</span>
              </button>

              <button
                style={{
                  ...styles.categoryTab,
                  ...(selectedCategory === 'contribution' ? styles.contributionTab : {}),
                }}
                onClick={() => setSelectedCategory('contribution')}
              >
                <span>❤️</span>
                <span>Contributions ({contributionCount})</span>
              </button>
            </div>

            {/* Sort Button */}
            <div style={styles.filtersRow}>
              <button style={styles.sortButton}>
                <span>🕒</span>
                <span>{sortOptions.find(s => s.id === sortBy)?.name || 'Sort'}</span>
              </button>
            </div>
          </div>

          {/* Chamas Grid */}
          {filteredChamas.length > 0 ? (
            <div style={styles.chamasGrid}>
              {filteredChamas.map(renderChamaCard)}
            </div>
          ) : (
            renderEmptyState()
          )}

          {/* FAB */}
          <button
            style={styles.fab}
            onMouseDown={handleDragStart}
            onTouchStart={handleDragStart}
            onClick={(e) => {
              if (!isDragging) {
                handleChamaAction('create');
              }
            }}
          >
            <span style={{ fontSize: 24, color: colors.white }}>➕</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default MyChamas;
